import { useState } from "react";
import { useApp } from "../context/AppContext";

const API_BASE = import.meta.env.VITE_API_URL || "";
export default function ServerAssignmentModal({
  model,
  onClose,
  guilds,
  assignments,
  setAssignments,
  loading,
  onChanged
}) {
  const { showToast } = useApp();
  const [processingId, setProcessingId] = useState(null);
  const [refreshTimer, setRefreshTimer] = useState(null);

  function scheduleRefresh() {
    if (!onChanged) return;
    if (refreshTimer) clearTimeout(refreshTimer);
    const t = setTimeout(() => {
      onChanged();
    }, 250);
    setRefreshTimer(t);
  }

  const handleAssign = (guild) => {
    setProcessingId(guild.id);
    fetch(`${API_BASE}/api/servers/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        guild_id: guild.id,
        guild_name: guild.name,
        guild_icon: guild.icon,
        user_model_id: model.id,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        if (data.bot_joined === false) {
          showToast("Config saved, but Bot needs to be invited to this server!", "info");
        } else {
          showToast(`Assigned to ${guild.name}`, "success");
        }
        setAssignments((prev) => ({
          ...(prev || {}),
          [guild.id]: { user_model_id: model.id, display_name: model.displayName }
        }));
        scheduleRefresh();
      })
      .catch((err) => showToast(err.message, "error"))
      .finally(() => setProcessingId(null));
  };

  const handleRemove = (guildId) => {
    setProcessingId(guildId);
    fetch(`${API_BASE}/api/servers/assign/${guildId}`, {
      method: "DELETE",
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        showToast("Assignment removed", "info");
        setAssignments((prev) => {
          const next = { ...prev };
          delete next[guildId];
          return next;
        });
        scheduleRefresh();
      })
      .catch((err) => showToast(err.message, "error"))
      .finally(() => setProcessingId(null));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
        <div className="modal-header">
          <h2>Server Mention Model</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: "0.9rem", color: "#a1a1aa", marginBottom: "0.75rem", lineHeight: "1.45" }}>
            Select a server to bind <strong>{model.displayName}</strong> as its Mention Model.
            When members @mention the bot in that server, this specific model (and its prompt) will reply.
          </div>

          <div
            style={{
              marginBottom: "1rem",
              padding: "0.9rem 1rem",
              borderRadius: "10px",
              background: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.28)",
              color: "#d4d4d8",
              fontSize: "0.9rem",
              lineHeight: "1.45"
            }}
          >
            <div style={{ fontWeight: 800, color: "#fbbf24", marginBottom: "0.35rem" }}>
              Attention!
            </div>
            <div style={{ marginTop: "0.25rem", color: "#a1a1aa", fontSize: "0.85rem" }}>
              Anyone in this server can @mention the bot, and replies use the server owner’s plan limits (Free: 20/day) and balance/costs.
            </div>
            <div style={{ marginTop: "0.4rem", color: "#a1a1aa", fontSize: "0.85rem" }}>
              This only affects @mentions. /chat still uses each person’s own account.
            </div>
          </div>

          <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: "8px" }}>
            {loading ? (
              <div className="spinner" style={{ margin: "2rem auto" }}></div>
            ) : !guilds || guilds.length === 0 ? (
              <div className="empty-state">
                {loading ? "Loading..." : "No servers found where you are Admin/Owner."}
              </div>
            ) : (
              guilds.map((guild) => {
                const assigned = assignments ? assignments[guild.id] : null;
                const assignedName = assigned ? assigned.display_name : null;
                const assignedModelId = assigned ? assigned.user_model_id : null;
                const isAssignedToThis = assignedModelId && String(assignedModelId) === String(model.id);
                const isAssignedToOther = assignedModelId && !isAssignedToThis;

                return (
                  <div key={guild.id} style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.75rem 1rem",
                    borderBottom: "1px solid var(--border-color)",
                    background: "var(--bg-primary)"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      {guild.icon ? (
                        <img
                          src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                          alt=""
                          style={{ width: "32px", height: "32px", borderRadius: "50%" }}
                        />
                      ) : (
                        <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#3f3f46", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem" }}>
                          {guild.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}

                      <div>
                        <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>{guild.name}</div>
                        {isAssignedToOther ? (
                          <div style={{ fontSize: "0.75rem", color: "#eab308" }}>
                            @mentions: {assignedName}
                          </div>
                        ) : isAssignedToThis ? (
                          <div style={{ fontSize: "0.75rem", color: "#22c55e" }}>
                            @mentions: {model.displayName}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <button
                      className={`btn ${isAssignedToThis ? "btn-danger" : "btn-primary"}`}
                      style={{ padding: "0.25rem 0.75rem", fontSize: "0.75rem" }}
                      disabled={processingId === guild.id}
                      onClick={() => isAssignedToThis ? handleRemove(guild.id) : handleAssign(guild)}
                    >
                      {isAssignedToThis ? "Unbind" : isAssignedToOther ? "Replace" : "Assign"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
